import {configureStore, combineReducers} from '@reduxjs/toolkit';
import {createMemoryHistory, createBrowserHistory} from 'history';
import {createReduxHistoryContext} from 'redux-first-history';
import {persistReducer} from 'redux-persist';
import {activate} from 'store/middleware/activate';
import {reporter} from 'store/middleware/reporter';
import filesystem from 'extensions/filesystem';
import storage from 'extensions/storage';
import slices from 'store/slices';
import {login} from 'utils/mediafire';
import {isLocal, isWeb} from 'utils/platform';
import {name} from 'config/app';

// https://github.com/salvoravida/redux-first-history#options
const {createReduxHistory, routerMiddleware, routerReducer} = createReduxHistoryContext({
  reduxTravelling: __DEV__,
  history: isWeb()
    ? createBrowserHistory()
    : createMemoryHistory(),
});

// https://github.com/rt2zz/redux-persist
const reducer = persistReducer({
  key: name,
  version: 1,
  storage: storage.init(name),
  blacklist: [
    'router',
    slices.app.name,
    slices.files.name,
    slices.transfers.name,
  ],
}, combineReducers({
  router: routerReducer,
  app: slices.app.reducer,
  files: slices.files.reducer,
  transfers: slices.transfers.reducer,
}));

// https://redux.js.org/understanding/history-and-design/middleware
const {initActivate} = activate(slices);
const {initReporter, pluginReporter} = reporter(slices);

// https://redux.js.org/usage/configuring-your-store
const store = configureStore({
  reducer,
  devTools: isLocal(),
  middleware: [
    pluginReporter,
    routerMiddleware,
  ],
});

// https://github.com/remix-run/history/blob/dev/docs/api-reference.md
export const history = createReduxHistory(store);

(async () => {
  // Authenticate
  const token = await filesystem.getSecureItem('access_token');
  const user = await login(store.dispatch, token);

  // Flag app as loaded to remove splash
  store.dispatch(slices.app.actions.load({loaded: true}));

  // Start middleware
  initReporter(store, user);
  initActivate(store);

  // Reset folder view when navigating
  // TODO: refactor to middleware or in router
  history.listen(() => {
    store.dispatch(slices.files.actions.resetFolderView());
  });
})()

export type Store = ReturnType<typeof reducer>;
export {slices};
export default store;
